import axios from "axios";
import { v4 as uuid } from "uuid";

export type SessionTransitionKind =
  | "login"
  | "login-pending"
  | "logout"
  | "masquerade"
  | "unmasquerade"
  | "session-expired";

export interface SessionIdentity {
  user_id: string;
  name_given: string;
  name_family: string;
  email: string;
  masquerade: boolean;
}

export interface SessionChangeMessage {
  type: "session-changed";
  app_id: string;
  tab_id: string;
  sequence: number;
  changed_at: number;
  transition: SessionTransitionKind;
  before: SessionIdentity | null;
  after: SessionIdentity | null;
  after_known: boolean;
  pending_login_email: string | null;
}

export interface CurrentSessionResult {
  known: boolean;
  session: SessionIdentity | null;
}

type SessionListener = (message: SessionChangeMessage) => void;

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

export function isSessionIdentity(value: unknown): value is SessionIdentity {
  if (!value || typeof value !== "object") return false;
  const identity = value as SessionIdentity;
  return typeof identity.user_id === "string" && identity.user_id.length > 0 &&
    typeof identity.name_given === "string" &&
    typeof identity.name_family === "string" &&
    typeof identity.email === "string" &&
    typeof identity.masquerade === "boolean";
}

export function isSessionChangeMessage(value: unknown): value is SessionChangeMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as SessionChangeMessage;
  const transitions: SessionTransitionKind[] = [
    "login", "login-pending", "logout", "masquerade", "unmasquerade", "session-expired",
  ];
  return message.type === "session-changed" &&
    typeof message.app_id === "string" && message.app_id.length > 0 &&
    typeof message.tab_id === "string" && message.tab_id.length > 0 &&
    Number.isSafeInteger(message.sequence) && message.sequence > 0 &&
    Number.isSafeInteger(message.changed_at) && message.changed_at > 0 &&
    transitions.includes(message.transition) &&
    (message.before === null || isSessionIdentity(message.before)) &&
    (message.after === null || isSessionIdentity(message.after)) &&
    typeof message.after_known === "boolean" &&
    isNullableString(message.pending_login_email);
}

export function sessionIdentityFromState(state: {
  current_user?: Record<string, unknown> | null;
  masquerade?: boolean;
}): SessionIdentity | null {
  const user = state.current_user;
  if (!user || typeof user.id !== "string") return null;
  return {
    user_id: user.id,
    name_given: typeof user.name_given === "string" ? user.name_given : "",
    name_family: typeof user.name_family === "string" ? user.name_family : "",
    email: typeof user.email === "string" ? user.email : "",
    masquerade: Boolean(state.masquerade),
  };
}

export function sessionsMatch(a: SessionIdentity | null, b: SessionIdentity | null): boolean {
  if (a === null || b === null) return a === b;
  return a.user_id === b.user_id && a.masquerade === b.masquerade;
}

export async function fetchCurrentSession(): Promise<CurrentSessionResult> {
  try {
    const response = await axios.get("/api/user/current", {
      __skip_auth_check: true,
      __skip_session_sync: true,
    } as object);
    if (response.status !== 200 || response.data?.status !== "success") {
      return { known: false, session: null };
    }
    const current = response.data.data;
    if (current === null) return { known: true, session: null };
    if (!current?.user || typeof current.masquerade !== "boolean") {
      return { known: false, session: null };
    }
    const session = sessionIdentityFromState({
      current_user: current.user,
      masquerade: current.masquerade,
    });
    return session ? { known: true, session } : { known: false, session: null };
  } catch (_) {
    return { known: false, session: null };
  }
}

function compareMessages(a: SessionChangeMessage, b: SessionChangeMessage): number {
  return a.changed_at - b.changed_at ||
    a.sequence - b.sequence ||
    (a.tab_id < b.tab_id ? -1 : a.tab_id === b.tab_id ? 0 : 1);
}

export class SessionSync {
  private sequence = 0;
  private latest: SessionChangeMessage | null = null;
  private channel: BroadcastChannel | null = null;
  private listeners = new Set<SessionListener>();

  constructor(readonly app_id: string, readonly tab_id = uuid()) {}

  create(
    transition: SessionTransitionKind,
    before: SessionIdentity | null,
    after: SessionIdentity | null,
    after_known = true,
    pending_login_email: string | null = null,
  ): SessionChangeMessage {
    return {
      type: "session-changed",
      app_id: this.app_id,
      tab_id: this.tab_id,
      sequence: ++this.sequence,
      changed_at: Date.now(),
      transition,
      before,
      after,
      after_known,
      pending_login_email,
    };
  }

  accept(message: SessionChangeMessage): boolean {
    this.sequence = Math.max(this.sequence, message.sequence);
    if (this.latest && compareMessages(this.latest, message) >= 0) return false;
    this.latest = message;
    return true;
  }

  publish(message: SessionChangeMessage) {
    this.accept(message);
    this.channel?.postMessage(message);
  }

  notifySessionExpired(before: SessionIdentity | null) {
    const message = this.create("session-expired", before, null);
    if (!this.accept(message)) return;
    this.listeners.forEach(listener => listener(message));
  }

  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    if (!this.channel && typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(`opticalgym:${this.app_id}:session`);
      this.channel.onmessage = ({ data }) => {
        if (isSessionChangeMessage(data) &&
          data.app_id === this.app_id &&
          data.tab_id !== this.tab_id &&
          this.accept(data)) {
          this.listeners.forEach(callback => callback(data));
        }
      };
    }
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        this.channel?.close();
        this.channel = null;
      }
    };
  }
}

const applications = new Map<string, SessionSync>();

export function sessionSync(app_id: string): SessionSync {
  if (!applications.has(app_id)) applications.set(app_id, new SessionSync(app_id));
  return applications.get(app_id)!;
}

function requestData(response: any): Record<string, any> {
  const data = response?.config?.data;
  if (!data) return {};
  if (typeof data === "object") return data;
  try {
    return JSON.parse(data);
  } catch (_) {
    return {};
  }
}

function requestPath(response: any): string {
  const url = response?.config?.url;
  if (typeof url !== "string") return "";
  try {
    return new URL(url, window.location.origin).pathname;
  } catch (_) {
    return url.split("?")[0];
  }
}

export function sessionTransitionForResponse(response: any): {
  transition: SessionTransitionKind;
  pending_login_email: string | null;
} | null {
  if (response?.config?.__skip_session_sync ||
    response?.data?.status !== "success" ||
    String(response?.config?.method || "").toLowerCase() !== "post") {
    return null;
  }

  const path = requestPath(response);
  const data = requestData(response);

  if (path === "/api/user/login") {
    if (!response.data.data?.success) return null;
    return {
      transition: response.data.data.tfa_required ? "login-pending" : "login",
      pending_login_email: typeof data.email === "string" ? data.email : null,
    };
  }
  if (/^\/api\/tfa\/redeem\/[^/]+$/.test(path) ||
    path === "/api/invite/token/login" ||
    /^\/api\/invite\/[^/]+\/accept\/[^/]+$/.test(path) ||
    path === "/api/clinic/signup" ||
    /^\/api\/signup\/[^/]+$/.test(path) ||
    path === "/api/user/password_reset") {
    return { transition: "login", pending_login_email: null };
  }
  if (path === "/api/user/logout") {
    return { transition: "logout", pending_login_email: null };
  }
  if (path === "/api/user/masquerade") {
    return { transition: "masquerade", pending_login_email: null };
  }
  if (path === "/api/user/unmasquerade") {
    return { transition: "unmasquerade", pending_login_email: null };
  }
  return null;
}

export async function publishSessionTransition(response: any, store: any): Promise<void> {
  const descriptor = sessionTransitionForResponse(response);
  if (!descriptor) return;

  const app_id = store?.state?.app_config?.id || window.app_config?.id;
  if (!app_id) return;

  const before = sessionIdentityFromState(store.state);
  if (descriptor.transition === "login-pending") {
    sessionSync(app_id).publish(sessionSync(app_id).create(
      descriptor.transition,
      before,
      null,
      false,
      descriptor.pending_login_email,
    ));
    return;
  }

  const current = await fetchCurrentSession();
  sessionSync(app_id).publish(sessionSync(app_id).create(
    descriptor.transition,
    before,
    current.session,
    current.known,
    descriptor.pending_login_email,
  ));
}
